import { useState, useEffect, useMemo, useCallback, useRef } from 'react';

// Types for vehicle make/model data
export interface VehicleMake {
	id: string;
	name: string;
}

export interface VehicleModel {
	id: string;
	name: string;
	makeId: string;
}

interface NHTSAMakeResult {
	Make_ID: number;
	Make_Name: string;
}

interface NHTSAModelResult {
	Make_ID: number;
	Make_Name: string;
	Model_ID: number;
	Model_Name: string;
}

interface NHTSAMakesResponse {
	Results: NHTSAMakeResult[];
}

interface NHTSAModelsResponse {
	Results: NHTSAModelResult[];
}

// Popular European and global makes to prioritize (sorted by European market share)
const POPULAR_MAKES = new Set([
	// European brands
	'VOLKSWAGEN', 'BMW', 'MERCEDES-BENZ', 'AUDI', 'OPEL', 'PEUGEOT', 'RENAULT',
	'CITROEN', 'FIAT', 'SEAT', 'SKODA', 'VOLVO', 'PORSCHE', 'MINI', 'ALFA ROMEO',
	'LANCIA', 'SMART', 'DACIA', 'SAAB', 'JAGUAR', 'LAND ROVER', 'BENTLEY',
	'ROLLS-ROYCE', 'FERRARI', 'LAMBORGHINI', 'MASERATI', 'ASTON MARTIN', 'LOTUS',
	'MCLAREN', 'BUGATTI', 'DS',
	// Japanese brands popular in Europe
	'TOYOTA', 'HONDA', 'NISSAN', 'MAZDA', 'MITSUBISHI', 'SUZUKI', 'LEXUS',
	'SUBARU', 'INFINITI',
	// Korean brands
	'HYUNDAI', 'KIA',
	// American brands with EU presence
	'FORD', 'JEEP', 'CHEVROLET', 'TESLA',
	// Chinese brands entering EU market
	'BYD', 'MG', 'NIO', 'XPENG', 'POLESTAR'
]);

// Mapping of car makes to their domain names for logo fetching via Clearbit
const MAKE_TO_DOMAIN: Record<string, string> = {
	// European brands
	'VOLKSWAGEN': 'volkswagen.com',
	'BMW': 'bmw.com',
	'MERCEDES-BENZ': 'mercedes-benz.com',
	'MERCEDES': 'mercedes-benz.com',
	'AUDI': 'audi.com',
	'OPEL': 'opel.com',
	'PEUGEOT': 'peugeot.com',
	'RENAULT': 'renault.com',
	'CITROEN': 'citroen.com',
	'CITROËN': 'citroen.com',
	'FIAT': 'fiat.com',
	'SEAT': 'seat.com',
	'CUPRA': 'cupraofficial.com',
	'SKODA': 'skoda-auto.com',
	'ŠKODA': 'skoda-auto.com',
	'VOLVO': 'volvocars.com',
	'PORSCHE': 'porsche.com',
	'MINI': 'mini.com',
	'ALFA ROMEO': 'alfaromeo.com',
	'LANCIA': 'lancia.com',
	'SMART': 'smart.com',
	'DACIA': 'dacia.com',
	'SAAB': 'saab.com',
	'JAGUAR': 'jaguar.com',
	'LAND ROVER': 'landrover.com',
	'BENTLEY': 'bentleymotors.com',
	'ROLLS-ROYCE': 'rolls-roycemotorcars.com',
	'FERRARI': 'ferrari.com',
	'LAMBORGHINI': 'lamborghini.com',
	'MASERATI': 'maserati.com',
	'ASTON MARTIN': 'astonmartin.com',
	'LOTUS': 'lotuscars.com',
	'MCLAREN': 'mclaren.com',
	'BUGATTI': 'bugatti.com',
	'DS': 'dsautomobiles.com',
	// Japanese brands
	'TOYOTA': 'toyota.com',
	'HONDA': 'honda.com',
	'NISSAN': 'nissan.com',
	'MAZDA': 'mazda.com',
	'MITSUBISHI': 'mitsubishi-motors.com',
	'SUZUKI': 'suzuki.com',
	'LEXUS': 'lexus.com',
	'SUBARU': 'subaru.com',
	'INFINITI': 'infiniti.com',
	'ACURA': 'acura.com',
	'ISUZU': 'isuzu.com',
	'DAIHATSU': 'daihatsu.com',
	// Korean brands
	'HYUNDAI': 'hyundai.com',
	'KIA': 'kia.com',
	'GENESIS': 'genesis.com',
	// American brands
	'FORD': 'ford.com',
	'JEEP': 'jeep.com',
	'CHEVROLET': 'chevrolet.com',
	'TESLA': 'tesla.com',
	'CADILLAC': 'cadillac.com',
	'DODGE': 'dodge.com',
	'CHRYSLER': 'chrysler.com',
	'GMC': 'gmc.com',
	'BUICK': 'buick.com',
	'LINCOLN': 'lincoln.com',
	'RAM': 'ramtrucks.com',
	'HUMMER': 'hummer.com',
	// Chinese brands
	'BYD': 'byd.com',
	'MG': 'mgmotor.com',
	'NIO': 'nio.com',
	'XPENG': 'xpeng.com',
	'POLESTAR': 'polestar.com',
	'GEELY': 'geely.com',
	'GREAT WALL': 'gwm.com.cn',
	'CHERY': 'cheryinternational.com',
	// Other
	'RIVIAN': 'rivian.com',
	'LUCID': 'lucidmotors.com',
};

/**
 * Get the logo URL for a vehicle make using Brandfetch CDN (free, no API key required)
 * Uses the 'icon' variant which provides the actual brand icon/emblem
 * @param makeName The name of the vehicle make (e.g., "BMW", "Mercedes-Benz")
 * @returns The URL to the logo image, or null if no mapping exists
 */
export function getVehicleMakeLogoUrl(makeName: string | null | undefined): string | null {
	if (!makeName) return null;

	const upperName = makeName.toUpperCase().trim();
	const domain = MAKE_TO_DOMAIN[upperName];

	if (domain) {
		// Use Brandfetch CDN icon format - actual brand emblem/badge
		return `https://cdn.brandfetch.io/${domain}/icon`;
	}

	// Try to construct a domain from the make name as fallback
	const simplifiedName = upperName
		.toLowerCase()
		.replace(/[^a-z0-9]/g, '')
		.replace(/\s+/g, '');

	return `https://cdn.brandfetch.io/${simplifiedName}.com/icon`;
}

// Cache for makes and models to avoid repeated API calls
let makesCache: VehicleMake[] | null = null;
let makesCachePromise: Promise<VehicleMake[]> | null = null;
const modelsCache = new Map<string, VehicleModel[]>();
const modelsCachePromises = new Map<string, Promise<VehicleModel[]>>();

/**
 * Fetch all vehicle makes from NHTSA API
 */
async function fetchMakes(): Promise<VehicleMake[]> {
	// Return cached data if available
	if (makesCache) {
		return makesCache;
	}

	// Return existing promise if fetch is in progress
	if (makesCachePromise) {
		return makesCachePromise;
	}

	makesCachePromise = (async () => {
		try {
			const response = await fetch(
				'https://vpic.nhtsa.dot.gov/api/vehicles/getallmakes?format=json'
			);

			if (!response.ok) {
				throw new Error(`HTTP error! status: ${response.status}`);
			}

			const data: NHTSAMakesResponse = await response.json();

			const makes = data.Results.map((make) => ({
				id: String(make.Make_ID),
				name: make.Make_Name
			}));

			// Sort: popular makes first, then alphabetically
			makes.sort((a, b) => {
				const aPopular = POPULAR_MAKES.has(a.name.toUpperCase());
				const bPopular = POPULAR_MAKES.has(b.name.toUpperCase());

				if (aPopular && !bPopular) return -1;
				if (!aPopular && bPopular) return 1;
				return a.name.localeCompare(b.name);
			});

			makesCache = makes;
			return makes;
		} catch (error) {
			makesCachePromise = null; // Allow retry on error
			throw error;
		}
	})();

	return makesCachePromise;
}

/**
 * Fetch vehicle models for a specific make from NHTSA API
 */
async function fetchModels(makeName: string): Promise<VehicleModel[]> {
	const cacheKey = makeName.toLowerCase();

	// Return cached data if available
	if (modelsCache.has(cacheKey)) {
		return modelsCache.get(cacheKey)!;
	}

	// Return existing promise if fetch is in progress
	if (modelsCachePromises.has(cacheKey)) {
		return modelsCachePromises.get(cacheKey)!;
	}

	const promise = (async () => {
		try {
			const encodedMake = encodeURIComponent(makeName);
			const response = await fetch(
				`https://vpic.nhtsa.dot.gov/api/vehicles/getmodelsformake/${encodedMake}?format=json`
			);

			if (!response.ok) {
				throw new Error(`HTTP error! status: ${response.status}`);
			}

			const data: NHTSAModelsResponse = await response.json();

			// Deduplicate models by name (API sometimes returns duplicates)
			const uniqueModels = new Map<string, VehicleModel>();

			for (const model of data.Results) {
				const normalizedName = model.Model_Name.toUpperCase();
				if (!uniqueModels.has(normalizedName)) {
					uniqueModels.set(normalizedName, {
						id: String(model.Model_ID),
						name: model.Model_Name,
						makeId: String(model.Make_ID)
					});
				}
			}

			const models = Array.from(uniqueModels.values()).sort((a, b) =>
				a.name.localeCompare(b.name)
			);

			modelsCache.set(cacheKey, models);
			return models;
		} catch (error) {
			modelsCachePromises.delete(cacheKey); // Allow retry on error
			throw error;
		}
	})();

	modelsCachePromises.set(cacheKey, promise);
	return promise;
}

/**
 * Hook to fetch and filter vehicle makes
 */
export function useVehicleMakes(searchTerm: string = '') {
	const [makes, setMakes] = useState<VehicleMake[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<Error | null>(null);

	useEffect(() => {
		let cancelled = false;

		setLoading(true);
		setError(null);

		fetchMakes()
			.then((data) => {
				if (!cancelled) {
					setMakes(data);
					setLoading(false);
				}
			})
			.catch((err) => {
				if (!cancelled) {
					setError(err);
					setLoading(false);
				}
			});

		return () => {
			cancelled = true;
		};
	}, []);

	// Filter makes based on search term
	const filteredMakes = useMemo(() => {
		if (!searchTerm.trim()) {
			// When no search term, show popular makes first (limited to 50 for performance)
			return makes.slice(0, 50);
		}

		const term = searchTerm.toLowerCase();
		return makes.filter((make) =>
			make.name.toLowerCase().includes(term)
		).slice(0, 50);
	}, [makes, searchTerm]);

	return { makes: filteredMakes, allMakes: makes, loading, error };
}

/**
 * Hook to fetch and filter vehicle models for a specific make
 */
export function useVehicleModels(makeName: string | null, searchTerm: string = '') {
	const [models, setModels] = useState<VehicleModel[]>([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<Error | null>(null);
	const lastMakeRef = useRef<string | null>(null);

	useEffect(() => {
		if (!makeName) {
			setModels([]);
			setLoading(false);
			return;
		}

		// Avoid refetching if make hasn't changed
		if (lastMakeRef.current === makeName && models.length > 0) {
			return;
		}

		let cancelled = false;
		lastMakeRef.current = makeName;

		setLoading(true);
		setError(null);

		fetchModels(makeName)
			.then((data) => {
				if (!cancelled) {
					setModels(data);
					setLoading(false);
				}
			})
			.catch((err) => {
				if (!cancelled) {
					setError(err);
					setLoading(false);
				}
			});

		return () => {
			cancelled = true;
		};
	}, [makeName]);

	// Filter models based on search term
	const filteredModels = useMemo(() => {
		if (!searchTerm.trim()) {
			return models;
		}

		const term = searchTerm.toLowerCase();
		return models.filter((model) =>
			model.name.toLowerCase().includes(term)
		);
	}, [models, searchTerm]);

	return { models: filteredModels, allModels: models, loading, error };
}

/**
 * Combined hook for make-dependent model selection
 */
export function useVehicleMakeModel() {
	const [selectedMake, setSelectedMake] = useState<string>('');
	const [selectedModel, setSelectedModel] = useState<string>('');
	const [makeSearchTerm, setMakeSearchTerm] = useState('');
	const [modelSearchTerm, setModelSearchTerm] = useState('');

	const { makes, loading: makesLoading, error: makesError } = useVehicleMakes(makeSearchTerm);
	const { models, loading: modelsLoading, error: modelsError } = useVehicleModels(
		selectedMake || null,
		modelSearchTerm
	);

	const handleMakeChange = useCallback((make: string) => {
		setSelectedMake(make);
		setSelectedModel(''); // Reset model when make changes
		setModelSearchTerm('');
	}, []);

	const handleModelChange = useCallback((model: string) => {
		setSelectedModel(model);
	}, []);

	return {
		// Make
		selectedMake,
		setSelectedMake: handleMakeChange,
		makes,
		makesLoading,
		makesError,
		makeSearchTerm,
		setMakeSearchTerm,
		// Model
		selectedModel,
		setSelectedModel: handleModelChange,
		models,
		modelsLoading,
		modelsError,
		modelSearchTerm,
		setModelSearchTerm
	};
}

// Utility to clear caches (useful for testing or forced refresh)
export function clearVehicleCaches() {
	makesCache = null;
	makesCachePromise = null;
	modelsCache.clear();
	modelsCachePromises.clear();
}
